import { CONFIG } from '../app/config.js';

export class RunnerController {
  constructor(character, options = {}) {
    this.character = character;
    this.onPauseToggle = options.onPauseToggle || (() => {});

    this.currentLaneIndex = 1; // 0: Left, 1: Center, 2: Right
    this.jumpHeight = 0;
    this.jumpVelocity = 0;
    this.isGrounded = true;
    this.enabled = false;

    // Swipe detection coordinates
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchStartTime = 0;

    this.onKeyDown = this.onKeyDown.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
  }

  attachListeners(domElement = window) {
    this.targetElement = domElement;
    window.addEventListener('keydown', this.onKeyDown, false);
    domElement.addEventListener('touchstart', this.onTouchStart, { passive: true });
    domElement.addEventListener('touchend', this.onTouchEnd, { passive: true });
  }

  detachListeners() {
    window.removeEventListener('keydown', this.onKeyDown);
    if (this.targetElement) {
      this.targetElement.removeEventListener('touchstart', this.onTouchStart);
      this.targetElement.removeEventListener('touchend', this.onTouchEnd);
    }
  }

  reset() {
    this.currentLaneIndex = 1;
    this.jumpHeight = 0;
    this.jumpVelocity = 0;
    this.isGrounded = true;
    this.enabled = true;

    if (this.character) {
      this.character.setLane(CONFIG.LANES.COORDS[1]);
      this.character.setJumpHeight(0);
    }
  }

  setEnabled(enabled) {
    this.enabled = enabled;
  }

  moveLeft() {
    if (!this.enabled) return;
    if (this.currentLaneIndex > 0) {
      this.currentLaneIndex--;
      this.syncLane();
    }
  }

  moveRight() {
    if (!this.enabled) return;
    if (this.currentLaneIndex < CONFIG.LANES.COUNT - 1) {
      this.currentLaneIndex++;
      this.syncLane();
    }
  }

  jump() {
    if (!this.enabled) return;
    if (this.isGrounded) {
      this.isGrounded = false;
      this.jumpVelocity = CONFIG.RUNNER.JUMP_IMPULSE;
    }
  }

  syncLane() {
    const laneX = CONFIG.LANES.COORDS[this.currentLaneIndex];
    if (this.character) {
      this.character.setLane(laneX);
    }
  }

  getLaneX() {
    return CONFIG.LANES.COORDS[this.currentLaneIndex];
  }

  update(dt) {
    if (!this.isGrounded) {
      this.jumpVelocity += CONFIG.RUNNER.GRAVITY * dt;
      this.jumpHeight += this.jumpVelocity * dt;

      if (this.jumpHeight <= 0) {
        this.jumpHeight = 0;
        this.jumpVelocity = 0;
        this.isGrounded = true;
      }
    }

    if (this.character) {
      this.character.setJumpHeight(this.jumpHeight);
      this.character.updateAnimation(dt, this.enabled);
    }
  }

  onKeyDown(e) {
    if (!this.enabled && e.code !== 'Escape' && e.code !== 'KeyP') return;

    switch (e.code) {
      case 'KeyA':
      case 'ArrowLeft':
        e.preventDefault();
        this.moveLeft();
        break;
      case 'KeyD':
      case 'ArrowRight':
        e.preventDefault();
        this.moveRight();
        break;
      case 'Space':
      case 'ArrowUp':
      case 'KeyW':
        e.preventDefault();
        this.jump();
        break;
      case 'Escape':
      case 'KeyP':
        e.preventDefault();
        this.onPauseToggle();
        break;
    }
  }

  onTouchStart(e) {
    if (!this.enabled || !e.touches || e.touches.length === 0) return;
    const touch = e.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchStartTime = performance.now();
  }

  onTouchEnd(e) {
    if (!this.enabled || !e.changedTouches || e.changedTouches.length === 0) return;
    const touch = e.changedTouches[0];
    const dx = touch.clientX - this.touchStartX;
    const dy = touch.clientY - this.touchStartY;
    const duration = performance.now() - this.touchStartTime;

    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    // Filter out long drags; check minimum distance threshold of 30px
    if (duration > 600 || (absDx < 30 && absDy < 30)) return;

    if (absDx > absDy) {
      // Horizontal swipe
      if (dx < 0) {
        this.moveLeft();
      } else {
        this.moveRight();
      }
    } else {
      // Vertical swipe (Upward jump)
      if (dy < 0) {
        this.jump();
      }
    }
  }

  dispose() {
    this.detachListeners();
  }
}
